/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Service for calculating and managing user statistics. Handles metrics
like CO2 offset, distance traveled, and ride history analytics.
*/

import { toast } from "sonner";
import { UserStatistics, UserCounts } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { CO2_CONVERSION_FACTOR } from "@/utils/dashboardUtils";

// User statistics service
export const userStatsService = {
  // Get user statistics
  getUserStatistics: async (): Promise<UserStatistics> => {
    try {
      console.log("Fetching user statistics");
      
      // Fetch user data
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, role, username');
      
      if (usersError) throw usersError;
      
      const totalUsers = usersData.length;
      const totalAdmins = usersData.filter(user => user.role === "admin").length;
      const totalStandardUsers = totalUsers - totalAdmins;
      
      // Query rides table directly for user statistics
      const { data: ridesData, error: ridesError } = await supabase
        .from('rides')
        .select(`
          id,
          user_id,
          booking_id,
          distance_traveled_km,
          count_towards_offset,
          users (username)
        `);
        
      if (ridesError) {
        console.error("Error fetching rides data:", ridesError);
        throw ridesError;
      }
      
      console.log("Rides data count:", ridesData?.length || 0);
      
      // Create maps to track user statistics
      const userDistances = new Map();
      const userRideCounts = new Map();
      const userOffsetDistances = new Map();
      const userBookingCounts = new Map();
      
      // Process ride data to calculate statistics
      if (Array.isArray(ridesData) && ridesData.length > 0) {
        ridesData.forEach(ride => {
          const userId = ride.user_id;
          const username = ride.users?.username || 'Unknown';
          // Ensure distance is a number or default to 0
          const distance = Number(ride.distance_traveled_km) || 0;
          const countTowardsOffset = ride.count_towards_offset !== false; // Default to true if not specified
          
          // Initialize maps for new users
          if (!userDistances.has(userId)) {
            userDistances.set(userId, { 
              distance: 0, 
              username,
              offsetDistance: 0,
              nonOffsetDistance: 0
            });
            userRideCounts.set(userId, 0);
            userOffsetDistances.set(userId, 0);
            userBookingCounts.set(userId, new Set());
          }
          
          // Update total distance
          const userInfo = userDistances.get(userId);
          userInfo.distance += distance;
          
          // Update offset or non-offset distance
          if (countTowardsOffset) {
            userInfo.offsetDistance += distance;
          } else {
            userInfo.nonOffsetDistance += distance;
          }
          
          userDistances.set(userId, userInfo);
          
          // Increment ride count
          userRideCounts.set(userId, userRideCounts.get(userId) + 1);
          
          // Update offset distance if applicable
          if (countTowardsOffset) {
            userOffsetDistances.set(userId, userOffsetDistances.get(userId) + distance);
          }
          
          // Track unique booking IDs for this user
          if (ride.booking_id) {
            userBookingCounts.get(userId).add(ride.booking_id);
          }
        });
      }
      
      // Find most active user by booking count
      let maxBookings = 0;
      let mostActiveUserId = null;
      
      // Convert sets to counts and find the most active user
      userBookingCounts.forEach((bookingSet, userId) => {
        const bookingCount = bookingSet.size;
        if (bookingCount > maxBookings) {
          maxBookings = bookingCount;
          mostActiveUserId = userId;
        }
      });
      
      // Get username and distance of most active user
      const mostActiveUserInfo = userDistances.get(mostActiveUserId) || { username: 'N/A', distance: 0, offsetDistance: 0, nonOffsetDistance: 0 };
      const mostActiveUser = mostActiveUserInfo.username;
      const mostActiveDistance = mostActiveUserInfo.distance;
      
      // Calculate totals
      const totalDistance = Array.from(userDistances.values()).reduce((sum, info) => sum + info.distance, 0);
      const totalOffsetDistance = Array.from(userDistances.values()).reduce((sum, info) => sum + info.offsetDistance, 0);
      const totalNonOffsetDistance = Array.from(userDistances.values()).reduce((sum, info) => sum + info.nonOffsetDistance, 0);
      const totalRides = Array.from(userRideCounts.values()).reduce((sum, count) => sum + count, 0);
      
      // Calculate CO2 offset
      const totalCO2Offset = totalOffsetDistance * CO2_CONVERSION_FACTOR;
      
      // Calculate averages (handle division by zero)
      const averageRidesPerUser = totalUsers > 0 ? totalRides / totalUsers : 0;
      const averageDistancePerUser = totalUsers > 0 ? totalDistance / totalUsers : 0;
      const averageOffsetDistancePerUser = totalUsers > 0 ? totalOffsetDistance / totalUsers : 0;
      
      const statistics: UserStatistics = {
        totalUsers,
        totalAdmins,
        totalStandardUsers,
        mostActiveUser,
        mostActiveDistance,
        averageRidesPerUser: Number(averageRidesPerUser.toFixed(1)),
        averageDistancePerUser: Number(averageDistancePerUser.toFixed(2)),
        averageOffsetDistancePerUser: Number(averageOffsetDistancePerUser.toFixed(2)),
        totalOffsetDistance: Number(totalOffsetDistance.toFixed(2)),
        totalNonOffsetDistance: Number(totalNonOffsetDistance.toFixed(2)),
        totalCO2Offset: Number(totalCO2Offset.toFixed(2))
      };
      
      console.log("Successfully fetched user statistics:", statistics);
      return statistics;
    } catch (error) {
      console.error("Error fetching user statistics:", error);
      toast.error("Failed to load statistics. Please try refreshing the page.");
      throw error;
    }
  },
  
  // Count users by role
  countUsersByRole: async (): Promise<UserCounts> => {
    try {
      console.log("Fetching user role counts from Supabase...");
      
      const { data, error } = await supabase
        .from('users')
        .select('role');

      if (error) {
        console.error("Error fetching user roles:", error);
        throw error;
      }

      console.log("Raw user roles data:", data);
      console.log("Total users found in countUsersByRole:", data.length);
      
      // Count users by role
      const counts = {
        admin: data.filter(user => user.role === 'admin').length,
        user: data.filter(user => user.role === 'user').length
      };
      
      console.log("Calculated role counts:", counts);
      
      return counts;
    } catch (error) {
      console.error("Error counting users by role:", error);
      toast.error("Failed to count users");
      throw error;
    }
  },
};
